/**
 * Operation request/response types for Cashero wallet↔mint protocol.
 *
 * These are the inner rumor kinds (2293/2681) that get NIP-59 gift-wrapped.
 */

import { OP_REQUEST_KIND, OP_RESPONSE_KIND } from './constants';

// ====================================================================
// Operation Request Types (wallet → mint)
// ====================================================================

/** A blinded message to be signed by the mint */
export interface BlindedMessageData {
  /** The blinded point B_ (hex-encoded uncompressed secp256k1 point) */
  B_: string;
  /** The amount this message represents (piconero) */
  amount: number;
}

/** A blinded signature returned by the mint */
export interface BlindedSignatureData {
  /** The blinded signature point C_ (hex-encoded) */
  C_: string;
  /** The keyset ID */
  id: string;
}

/** Request: get the mint's keysets (no amount needed) */
export interface GetKeysetsRequest {
  op: 'get_keysets';
}

/** Request: initiate a deposit (get a Monero address to send to) */
export interface RequestDepositRequest {
  op: 'request_deposit';
  /** Unique request ID (uuid/hex) */
  requestId: string;
  /** The amount the user wants to deposit (piconero) */
  amount: number;
  /** Blinded messages for the ecash to be minted after deposit confirms */
  blindedMessages: BlindedMessageData[];
}

/** Request: swap proofs at the mint (for send/receive) */
export interface SwapRequest {
  op: 'swap';
  requestId: string;
  /** Proofs to swap (the old tokens being spent) */
  proofs: { secret: string; C: string; id: string; amount: number }[];
  /** New blinded messages to receive */
  blindedMessages: BlindedMessageData[];
}

/** Request: withdraw XMR to a Monero address */
export interface WithdrawRequest {
  op: 'withdraw';
  requestId: string;
  /** Proofs to redeem (the ecash being burned) */
  proofs: { secret: string; C: string; id: string; amount: number }[];
  /** Destination Monero address */
  destinationAddress: string;
  /** Change blinded messages (if proofs exceed amount+fee) */
  blindedMessages?: BlindedMessageData[];
}

/** Request: check if secrets have been spent */
export interface CheckRequest {
  op: 'check';
  requestId: string;
  /** Array of SHA-256 hashes of secrets to check */
  secretHashes: string[];
}

/** Union of all request types */
export type OperationRequest =
  | GetKeysetsRequest
  | RequestDepositRequest
  | SwapRequest
  | WithdrawRequest
  | CheckRequest;

// ====================================================================
// Operation Response Types (mint → wallet)
// ====================================================================

/** Response: keysets returned successfully */
export interface GetKeysetsResponse {
  op: 'get_keysets';
  status: 'ok';
  keysets: { id: string; unit: string; pubkeys: Record<number, string>; active: boolean }[];
}

/** Response: deposit address provided (waiting for XMR) */
export interface DepositAddressResponse {
  op: 'request_deposit';
  status: 'ok';
  requestId: string;
  /** The Monero subaddress to send XMR to */
  depositAddress: string;
  /** Optional payment ID (for older wallets) */
  paymentId?: string;
}

/** Response: deposit confirmed (ecash tokens issued) */
export interface DepositTokensResponse {
  op: 'request_deposit';
  status: 'ok';
  requestId: string;
  /** The blinded signatures (tokens) */
  blindedSignatures: BlindedSignatureData[];
  /** Transaction hash of the deposit */
  txHash?: string;
  /** Number of confirmations */
  confirmations: number;
}

/** Response: deposit status update */
export interface DepositStatusResponse {
  op: 'request_deposit';
  status: 'pending';
  requestId: string;
  /** Current number of confirmations */
  confirmations: number;
  /** Required confirmations */
  required: number;
  /** Transaction hash if detected */
  txHash?: string;
}

/** Response: swap completed successfully */
export interface SwapResponse {
  op: 'swap';
  status: 'ok';
  requestId: string;
  /** New blinded signatures */
  blindedSignatures: BlindedSignatureData[];
}

/** Response: withdrawal completed */
export interface WithdrawResponse {
  op: 'withdraw';
  status: 'ok';
  requestId: string;
  /** Monero transaction hash */
  txHash: string;
  /** Change signatures (if proofs exceeded amount+fee) */
  changeSignatures?: BlindedSignatureData[];
}

/** Response: check result */
export interface CheckResponse {
  op: 'check';
  status: 'ok';
  requestId: string;
  /** Whether each secret hash has been spent */
  spent: boolean[];
}

/** Response: error */
export interface ErrorResponse {
  op: string;
  status: 'error';
  requestId?: string;
  error: string;
}

/** Union of all response types */
export type OperationResponse =
  | GetKeysetsResponse
  | DepositAddressResponse
  | DepositTokensResponse
  | DepositStatusResponse
  | SwapResponse
  | WithdrawResponse
  | CheckResponse
  | ErrorResponse;

// ====================================================================
// Validation helpers
// ====================================================================

/** Check if a value is a valid OperationRequest */
export function isValidOperationRequest(value: unknown): value is OperationRequest {
  if (!value || typeof value !== 'object') return false;
  const obj = value as Record<string, unknown>;
  if (!obj.op || typeof obj.op !== 'string') return false;
  return true;
}

/** Check if a value is a valid OperationResponse */
export function isValidOperationResponse(value: unknown): value is OperationResponse {
  if (!value || typeof value !== 'object') return false;
  const obj = value as Record<string, unknown>;
  if (!obj.op || typeof obj.op !== 'string') return false;
  if (!obj.status || typeof obj.status !== 'string') return false;
  return true;
}

/** Get the rumor kind for a request */
export function getRequestKind(): number {
  return OP_REQUEST_KIND;
}

/** Get the rumor kind for a response */
export function getResponseKind(): number {
  return OP_RESPONSE_KIND;
}
